import { createContext, useContext, useEffect, useState, useCallback } from "react";

const TextStyleCtx = createContext(null);
const STORAGE_KEY = "aviary_text_style";

const DEFAULTS = {
  bold: false,
  size: "md",        // sm | md | lg
  fontColor: "",     // empty = inherit (default)
  cellBg: "",        // empty = use built-in cell colors
  align: "left",     // left | center | right
};

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULTS, ...parsed };
  } catch {
    return { ...DEFAULTS };
  }
}

export function TextStyleProvider({ children }) {
  const [style, setStyle] = useState(load);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(style)); } catch (_e) { void _e; }
    const body = document.body;
    // Bold class
    body.classList.toggle("aviary-bold", !!style.bold);
    // Size classes
    body.classList.remove("aviary-size-sm", "aviary-size-md", "aviary-size-lg");
    body.classList.add(`aviary-size-${style.size || "md"}`);
    // CSS variables for colors
    const root = document.documentElement;
    if (style.fontColor) {
      root.style.setProperty("--aviary-font-color", style.fontColor);
      body.classList.add("aviary-font-color-on");
    } else {
      root.style.removeProperty("--aviary-font-color");
      body.classList.remove("aviary-font-color-on");
    }
    if (style.cellBg) {
      root.style.setProperty("--aviary-cell-bg", style.cellBg);
      body.classList.add("aviary-cell-bg-on");
    } else {
      root.style.removeProperty("--aviary-cell-bg");
      body.classList.remove("aviary-cell-bg-on");
    }
    // Alignment classes
    body.classList.remove("aviary-align-left", "aviary-align-center", "aviary-align-right");
    body.classList.add(`aviary-align-${style.align || "left"}`);
  }, [style]);

  const update = useCallback((patch) => {
    setStyle((s) => ({ ...s, ...patch }));
  }, []);

  const reset = useCallback(() => {
    setStyle({ ...DEFAULTS });
  }, []);

  return (
    <TextStyleCtx.Provider value={{ style, update, reset }}>
      {children}
    </TextStyleCtx.Provider>
  );
}

export const useTextStyle = () => useContext(TextStyleCtx);
